import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClockIcon } from 'lucide-react';
const WINDOW_MS = 9 * 60 * 60 * 1000;
const STORAGE_KEY = 'hybrid_race_coach_ready_countdown_target';
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + WINDOW_MS;
  const storedTarget = window.localStorage.getItem(STORAGE_KEY);
  if (storedTarget) {
    const parsedTarget = Number.parseInt(storedTarget, 10);
    if (!Number.isNaN(parsedTarget) && parsedTarget > Date.now()) return parsedTarget;
  }
  const target = Date.now() + WINDOW_MS;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* storage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(WINDOW_MS);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + WINDOW_MS;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* storage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(WINDOW_MS);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = WINDOW_MS - remaining;
  const progress = Math.min(95, 81 + elapsed / WINDOW_MS * 14);
  return <motion.div initial={{
    opacity: 0,
    y: 16
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    amount: 0.3
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-charcoal border border-orange/50 rounded-2xl p-6 md:p-8 shadow-lg text-white" data-id="element-65" data-mp="2ihsma1tn9hgt">
      <div className="text-center" data-id="element-66" data-mp="2ja84p2i1ya49">
        <div className="flex items-center justify-center gap-2 text-orange font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-67" data-mp="2ed895litwpre">
          <AlarmClockIcon size={20} aria-hidden="true" data-id="element-68" data-mp="29en8jkzo042f" />
          <span data-id="element-69" data-mp="24n542myfnlcp">Launch pricing ends soon</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-70" data-mp="24lu3ew0aatpt">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-71" data-mp="1zovb8j06kwu9" />
          <span className="text-3xl md:text-5xl font-bold text-orange" data-id="element-72" data-mp="1vmgtk289034k">·</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-73" data-mp="1vkdtepddstm4" />
          <span className="text-3xl md:text-5xl font-bold text-orange" data-id="element-74" data-mp="1qnnggawvie4x">·</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-75" data-mp="1lokfb1k0ks28" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-white/70 mb-8" data-id="element-76" data-mp="1lqdpgqtu5uez">
          After this, the price increases and does not come back down.
        </p>
        <div className="space-y-2" data-id="element-77" data-mp="63s2qsp2d55d">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-78" data-mp="b4fytss5gc6g">
            <span className="text-white/70" data-id="element-79" data-mp="f5rgnc4s7t43">Launch price spots remaining</span>
            <span className="text-orange" data-id="element-80" data-mp="f3osyyr3qc2j">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-white/10 rounded-full overflow-hidden border border-orange/30" data-id="element-81" data-mp="3emzi45suo7p4">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-full bg-orange rounded-full" data-id="element-82" data-mp="3ji54f5q5zodq" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-white/70 font-medium" data-id="element-83" data-mp="2vsu9i4vhwo7i">
            {Math.round(progress)}% of spots have been claimed
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-graphite border-2 border-orange/40 rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[86px] md:min-w-[110px]" data-id="element-84" data-mp="2bqg0c9lauk48">
      <div className="flex items-baseline justify-center gap-1" data-id="element-85" data-mp="2lr02degn7sni">
        <span className="text-4xl md:text-5xl font-bold text-white tabular-nums" data-id="element-86" data-mp="2londehcgwi0y">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-orange" data-id="element-87" data-mp="2uj2198b2d0vo">
          {label}
        </span>
      </div>
    </div>;
}